<div class="container-fluid mt-4">
  <!-- Quiz Selection Card -->
  <div class="card my-4 quiz-list-container" *ngIf="!selectedQuiz">
    <div class="card-header">
      <h3>Review Quizzes</h3>
      <fa-icon class="card-header-icon bg-gradient-info text-white" [icon]="'chart-bar'" size="lg" [fixedWidth]="true"></fa-icon>
    </div>
    <div class="card-body">
      <!-- Search Bar -->
      <div class="row mb-4">
        <div class="col-12">
          <input
            type="text"
            class="form-control"
            placeholder="Search quizzes by name or path..."
            [(ngModel)]="searchTerm"
            (input)="filterQuizzes()"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoadingQuizzes" class="text-center py-4">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading quizzes...</span>
        </div>
      </div>

      <!-- Quiz List -->
      <div *ngIf="!isLoadingQuizzes && filteredQuizzes.length > 0" class="quiz-list">
        <div class="list-group">
          <button
            *ngFor="let quiz of filteredQuizzes"
            class="list-group-item list-group-item-action"
            [class.disabled]="!quiz.enabled"
            (click)="quiz.enabled && selectQuiz(quiz)"
          >
            <div class="d-flex w-100 justify-content-between align-items-center">
              <div class="flex-grow-1">
                <h6 class="mb-1">{{ quiz.displayName }}</h6>
                <small class="text-muted breadcrumb-path">
                  <span *ngFor="let crumb of quiz.breadcrumb; let i = index">
                    <span *ngIf="i > 0" class="mx-1">›</span>
                    {{ crumb }}
                  </span>
                </small>
              </div>
              <div>
                <span *ngIf="!quiz.enabled" class="badge bg-secondary">Disabled</span>
                <fa-icon [icon]="'chevron-right'" class="ms-2"></fa-icon>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- No Results -->
      <div *ngIf="!isLoadingQuizzes && filteredQuizzes.length === 0" class="text-center py-4">
        <p class="text-muted">
          <span *ngIf="searchTerm">No quizzes found matching "{{ searchTerm }}"</span>
          <span *ngIf="!searchTerm">No review quizzes available</span>
        </p>
      </div>
    </div>
  </div>

  <!-- Quiz Report Card -->
  <div class="card my-4" *ngIf="selectedQuiz">
    <div class="card-header d-flex justify-content-between align-items-center">
      <div>
        <h3>{{ selectedQuiz.displayName }}</h3>
        <small class="text-muted breadcrumb-path">
          <span *ngFor="let crumb of selectedQuiz.breadcrumb; let i = index">
            <span *ngIf="i > 0" class="mx-1">›</span>
            {{ crumb }}
          </span>
        </small>
      </div>
      <button class="btn btn-sm btn-outline-secondary" (click)="clearSelection()">Back to Quiz List</button>
    </div>
    <div class="card-body">
      <!-- Date Range Filter -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-4">
              <label for="dateFrom" class="form-label">From Date</label>
              <input type="date" id="dateFrom" class="form-control" [(ngModel)]="dateFrom" [max]="dateTo" (change)="onDateRangeChange()" />
            </div>
            <div class="col-md-4">
              <label for="dateTo" class="form-label">To Date</label>
              <input
                type="date"
                id="dateTo"
                class="form-control"
                [(ngModel)]="dateTo"
                [min]="dateFrom"
                [max]="today"
                (change)="onDateRangeChange()"
              />
            </div>
            <div class="col-md-4 text-end">
              <button class="btn btn-primary mt-2" (click)="searchWithDateRange()" [disabled]="isLoadingScores">
                <span *ngIf="isLoadingScores" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ isLoadingScores ? 'Searching...' : 'Search' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="Object.keys(data).length === 0" class="text-center py-4">
        <div class="spinner-border" role="status" *ngIf="isLoadingScores">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p *ngIf="!isLoadingScores" class="text-muted">No scores available for this quiz in the selected date range.</p>
      </div>

      <!-- Scores Report -->
      <div *ngIf="Object.keys(data).length > 0" class="scores-report">
        <!-- Export Buttons -->
        <div class="mb-3 d-flex justify-content-end">
          <div class="btn-group">
            <button type="button" class="btn btn-outline-primary" (click)="exportToCsv()">
              <fa-icon [icon]="'file-csv'" class="me-1"></fa-icon>Export CSV
            </button>
            <button type="button" class="btn btn-outline-danger" (click)="exportToPdf()">
              <fa-icon [icon]="'file-pdf'" class="me-1"></fa-icon>Export PDF
            </button>
          </div>
        </div>

        <!-- Scores Table -->
        <div class="table-responsive">
          <table class="table table-hover">
            <thead class="table-dark">
              <tr>
                <th scope="col" style="width: 30px"></th>
                <th scope="col">User</th>
                <th scope="col" class="text-center">Attempts</th>
                <th scope="col" class="text-center">Passed</th>
                <th scope="col" class="text-center">Failed</th>
                <th scope="col" class="text-center">Total Score</th>
                <th scope="col" class="text-center">Avg Score</th>
                <th scope="col" class="text-center">Total Time</th>
                <th scope="col" class="text-center">Avg Time</th>
                <th scope="col" class="text-center" *ngIf="isAdmin"></th>
              </tr>
            </thead>
            <tbody>
              <ng-container *ngFor="let userId of Object.keys(data); let i = index">
                <!-- User Summary Row -->
                <tr class="user-summary-row">
                  <td class="text-center">
                    <button
                      class="btn btn-sm btn-link p-0 expand-btn"
                      (click)="toggleUserExpansion(userId)"
                      [attr.aria-label]="isUserExpanded(userId) ? 'Collapse games' : 'Expand games'"
                      title="Click to expand/collapse individual games"
                    >
                      <fa-icon class="expand-icon" [icon]="isUserExpanded(userId) ? 'chevron-down' : 'chevron-right'"></fa-icon>
                    </button>
                  </td>
                  <td>
                    <strong>{{ data[userId].username }}</strong>
                    <span
                      *ngIf="getLockedUser(userId)"
                      class="badge bg-warning text-dark ms-2"
                      title="Failed all {{ reviewMaxAttempts }} attempts and cannot start this quiz again"
                      >Locked</span
                    >
                  </td>
                  <td class="text-center">{{ data[userId].totals.attempts }}</td>
                  <td class="text-center">
                    <span class="badge bg-success">{{ data[userId].totals.passed }}</span>
                  </td>
                  <td class="text-center">
                    <span class="badge bg-danger">{{ data[userId].totals.failed }}</span>
                  </td>
                  <td class="text-center">{{ data[userId].totals.score }}</td>
                  <td class="text-center">{{ data[userId].totals.averageScore | number:'1.1-1' }}</td>
                  <td class="text-center">{{ formatTime(data[userId].totals.totalTime) }}</td>
                  <td class="text-center">{{ formatTime(data[userId].totals.averageTime) }}</td>
                  <td class="text-center" *ngIf="isAdmin">
                    <button
                      *ngIf="getLockedUser(userId) as locked"
                      type="button"
                      class="btn btn-sm btn-outline-warning text-nowrap"
                      [disabled]="!canResetAttempts || isResetting(userId)"
                      [title]="canResetAttempts ? 'Delete all attempts so the user can take the quiz again' : 'Requires user read/write and stats read/write/delete permissions'"
                      (click)="resetAttempts(locked)"
                    >
                      <span *ngIf="isResetting(userId)" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      <fa-icon *ngIf="!isResetting(userId)" [icon]="'undo'" class="me-1"></fa-icon>Reset
                    </button>
                  </td>
                </tr>

                <!-- Individual Games Rows (Expandable) -->
                <tr *ngIf="isUserExpanded(userId)" class="games-header-row">
                  <td></td>
                  <td [attr.colspan]="isAdmin ? 9 : 8" class="px-4">
                    <div class="individual-games">
                      <h6 class="mb-3 text-muted">Individual Games</h6>
                      <div class="table-responsive">
                        <table class="table table-sm table-striped">
                          <thead class="table-light">
                            <tr>
                              <th class="text-center">Score</th>
                              <th class="text-center">Questions</th>
                              <th class="text-center">Correct</th>
                              <th class="text-center">Accuracy</th>
                              <th class="text-center">Time</th>
                              <th class="text-center">Status</th>
                              <th class="text-center">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr *ngFor="let game of data[userId].games">
                              <td class="text-center"><strong>{{ game.score }}</strong></td>
                              <td class="text-center">{{ game.total_questions }}</td>
                              <td class="text-center">{{ game.correct_answers }}</td>
                              <td class="text-center">{{ getAccuracyPercentage(game) | number:'1.0-0' }}%</td>
                              <td class="text-center">{{ formatTime(game.total_time) }}</td>
                              <td class="text-center">
                                <span
                                  class="badge"
                                  [class.bg-success]="game.passed"
                                  [class.bg-danger]="game.passed === false"
                                  [class.bg-secondary]="game.passed === undefined"
                                >
                                  {{ game.passed === true ? 'Passed' : game.passed === false ? 'Failed' : 'N/A' }}
                                </span>
                              </td>
                              <td class="text-center small">{{ game.game_started_at | date:'dd/MM/yyyy HH:mm:ss' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>

        <!-- Summary Statistics -->
        <div class="mt-4 p-3 bg-light rounded">
          <h6>Overall Statistics</h6>
          <div class="row">
            <div class="col-md-3">
              <div class="text-center">
                <div class="h4 text-primary">{{ getTotalUsers() }}</div>
                <small class="text-muted">Total Users</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="text-center">
                <div class="h4 text-info">{{ getTotalAttempts() }}</div>
                <small class="text-muted">Total Attempts</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="text-center">
                <div class="h4 text-success">{{ getTotalPassed() }}</div>
                <small class="text-muted">Total Passed</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="text-center">
                <div class="h4 text-warning">{{ getOverallAverageScore() | number:'1.1-1' }}</div>
                <small class="text-muted">Overall Avg Score</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Users Who Failed All Attempts (Admin Only) -->
      <div *ngIf="isAdmin" class="mt-4">
        <div class="p-3 border rounded">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <h6 class="mb-0 text-warning">
              Users who failed all {{ reviewMaxAttempts }} attempts
              <span class="badge bg-warning text-dark ms-2">{{ lockedUsers.length }}</span>
            </h6>
            <small class="text-muted">All time, independent of the selected date range</small>
          </div>

          <div *ngIf="isLoadingLocked" class="text-center py-2">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <ng-container *ngIf="!isLoadingLocked">
            <p *ngIf="!(reviewMaxAttempts > 0)" class="text-muted mb-0">Attempts are unlimited, so no user can be locked.</p>
            <p *ngIf="reviewMaxAttempts > 0 && lockedUsers.length === 0" class="text-muted mb-0">No users are locked out of this quiz.</p>

            <ng-container *ngIf="lockedUsers.length > 0">
              <div class="mb-3 d-flex justify-content-end">
                <div class="btn-group">
                  <button type="button" class="btn btn-outline-primary" (click)="exportLockedToCsv()">
                    <fa-icon [icon]="'file-csv'" class="me-1"></fa-icon>Export CSV
                  </button>
                  <button type="button" class="btn btn-outline-danger" (click)="exportLockedToPdf()">
                    <fa-icon [icon]="'file-pdf'" class="me-1"></fa-icon>Export PDF
                  </button>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-sm table-hover mb-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col">Username</th>
                      <th scope="col">Email</th>
                      <th scope="col" class="text-center">Attempts</th>
                      <th scope="col" class="text-center">Last Attempt</th>
                      <th scope="col" class="text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let user of lockedUsers">
                      <td>{{ user.username }}</td>
                      <td class="text-muted">{{ user.mail || '—' }}</td>
                      <td class="text-center">{{ user.attempts }}</td>
                      <td class="text-center small">{{ user.lastAttempt | date:'dd/MM/yyyy HH:mm:ss' }}</td>
                      <td class="text-center">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-warning text-nowrap"
                          [disabled]="!canResetAttempts || isResetting(user.userUuid)"
                          [title]="canResetAttempts ? 'Delete all attempts so the user can take the quiz again' : 'Requires user read/write and stats read/write/delete permissions'"
                          (click)="resetAttempts(user)"
                        >
                          <span *ngIf="isResetting(user.userUuid)" class="spinner-border spinner-border-sm me-1" role="status"></span>
                          <fa-icon *ngIf="!isResetting(user.userUuid)" [icon]="'undo'" class="me-1"></fa-icon>Reset
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ng-container>
          </ng-container>
        </div>
      </div>

      <!-- Users Who Have Not Completed the Quiz (Admin Only) -->
      <div *ngIf="isAdmin && !isLoadingScores && usersNotCompleted.length > 0" class="mt-4">
        <div class="p-3 border rounded">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h6 class="mb-0 text-danger">
              Users who have not completed this quiz
              <span class="badge bg-danger ms-2">{{ usersNotCompleted.length }}</span>
            </h6>
            <!-- Search bar — only shown when there are more than 10 records -->
            <input
              *ngIf="usersNotCompleted.length > 10"
              type="text"
              class="form-control form-control-sm w-auto"
              placeholder="Search username or email…"
              [(ngModel)]="notCompletedSearch"
              (ngModelChange)="notCompletedSearchChanged()"
            />
          </div>
          <div class="mb-3 d-flex justify-content-end">
            <div class="btn-group">
              <button type="button" class="btn btn-outline-primary" (click)="exportNotCompletedToCsv()">
                <fa-icon [icon]="'file-csv'" class="me-1"></fa-icon>Export CSV
              </button>
              <button type="button" class="btn btn-outline-danger" (click)="exportNotCompletedToPdf()">
                <fa-icon [icon]="'file-pdf'" class="me-1"></fa-icon>Export PDF
              </button>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th scope="col">Username</th>
                  <th scope="col">Email</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngIf="usersNotCompleted.length > 10; else allRows">
                  <tr *ngFor="let user of usersNotCompletedPaged">
                    <td>{{ user.username }}</td>
                    <td class="text-muted">{{ user.meta?.mail || '—' }}</td>
                  </tr>
                  <tr *ngIf="usersNotCompletedFiltered.length === 0">
                    <td colspan="2" class="text-center text-muted">No users match your search.</td>
                  </tr>
                </ng-container>
                <ng-template #allRows>
                  <tr *ngFor="let user of usersNotCompleted">
                    <td>{{ user.username }}</td>
                    <td class="text-muted">{{ user.meta?.mail || '—' }}</td>
                  </tr>
                </ng-template>
              </tbody>
            </table>
          </div>
          <!-- Pagination — only shown when there are more than 10 records -->
          <nav *ngIf="usersNotCompleted.length > 10 && notCompletedTotalPages > 1" class="mt-2">
            <ul class="pagination pagination-sm justify-content-end mb-0">
              <li class="page-item" [class.disabled]="notCompletedPage === 1">
                <button class="page-link" (click)="notCompletedPage = notCompletedPage - 1" [disabled]="notCompletedPage === 1">
                  &laquo;
                </button>
              </li>
              <li
                *ngFor="let p of [].constructor(notCompletedTotalPages); let i = index"
                class="page-item"
                [class.active]="notCompletedPage === i + 1"
              >
                <button class="page-link" (click)="notCompletedPage = i + 1">{{ i + 1 }}</button>
              </li>
              <li class="page-item" [class.disabled]="notCompletedPage === notCompletedTotalPages">
                <button
                  class="page-link"
                  (click)="notCompletedPage = notCompletedPage + 1"
                  [disabled]="notCompletedPage === notCompletedTotalPages"
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <!-- Settings Card -->
  <div class="card my-4">
    <div class="card-header">
      <h3>Review Game Mode Settings</h3>
      <fa-icon class="card-header-icon bg-gradient-primary text-white" icon="gamepad" size="lg" [fixedWidth]="true"></fa-icon>
    </div>
    <div class="card-body">
      <div class="row my-4">
        <label class="col-2 col-form-label">Maximum Attempts</label>
        <div class="col-10">
          <app-button-input
            [initialValue]="reviewMaxAttempts"
            [(value)]="newReviewMaxAttempts"
            [disabled]="reviewMaxAttempts === newReviewMaxAttempts"
            (apply)="updateReviewMaxAttempts(true)"
            (cancel)="updateReviewMaxAttempts(false)"
          ></app-button-input>
        </div>
      </div>
    </div>
  </div>
</div>
